import React, { useEffect, useState } from 'react';
import { fastapiService } from '../../services/fastapiService';
import { X, Coins } from 'lucide-react';

interface WorkspaceAddCreditsModalProps {
  workspaceId: string;
  workspaceName: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const WorkspaceAddCreditsModal: React.FC<WorkspaceAddCreditsModalProps> = ({
  workspaceId,
  workspaceName,
  isOpen,
  onClose,
  onSaved
}) => {
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && workspaceId) {
      loadBalance();
    }
  }, [isOpen, workspaceId]);

  const loadBalance = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fastapiService.getWorkspaceCredits(workspaceId);
      const data = res?.data ?? res;
      setBalance(typeof data?.balance === 'number' ? data.balance : (data?.data?.balance ?? null));
    } catch (e: any) {
      setError(e?.message || 'Failed to load balance');
      setBalance(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = parseInt(amount, 10);
    if (isNaN(num) || num <= 0) {
      setError('Please enter a valid positive number');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await fastapiService.addWorkspaceCredits(workspaceId, num, description || undefined);
      onSaved?.();
      setAmount('');
      setDescription('');
      loadBalance();
      onClose();
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to add credits');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-admin-200">
          <h2 className="text-lg font-semibold text-admin-900 flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-500" />
            Add Credits
          </h2>
          <button onClick={onClose} className="text-admin-500 hover:text-admin-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-admin-600">
            Add credits to <strong>{workspaceName}</strong>
            {balance !== null && (
              <span className="ml-1 text-admin-500">(Current balance: {balance})</span>
            )}
          </p>
          {loading && balance === null ? (
            <p className="text-admin-500 text-sm">Loading balance...</p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">Amount (credits)</label>
                <input
                  type="number"
                  min={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 100"
                  className="admin-input w-full"
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">Note (optional)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Promotional credits"
                  className="admin-input w-full"
                  disabled={saving}
                />
              </div>
              {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="admin-button">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !amount}
                  className="admin-button bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Adding...' : 'Add Credits'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};
